import { useForm } from 'react-hook-form';
import { FundRequestStatus } from '~/enums/fundRequest';

interface GenericStatusUpdateModalProps {
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleStatusUpdate: (
        status: FundRequestStatus,
        note: string | null
    ) => Promise<void>;
    status: FundRequestStatus;
    statusLabel: string;
}

const GenericStatusUpdateModal: React.FC<GenericStatusUpdateModalProps> = ({
    modalOpen,
    setModalOpen,
    handleStatusUpdate,
    status,
    statusLabel,
}) => {
    const { register, handleSubmit, formState, reset } = useForm({
        defaultValues: {
            note: '',
        },
    });

    const { isSubmitting } = formState;

    const onSubmit = async (data: any) => {
        await handleStatusUpdate(status, data.note || null);
        reset({ note: '' });
        setModalOpen(false);
    };

    return (
        <>
            {modalOpen && (
                <div
                    className='fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75'
                    onClick={() => setModalOpen(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
                    >
                        <div className='p-7'>
                            <h2 className='mb-4 text-3xl font-semibold'>
                                Update Status to {statusLabel}
                            </h2>
                            <p className='mb-4 text-gray-700'>
                                You are about to change the status of this fund request to{' '}
                                <strong>{statusLabel}</strong>. Please add any relevant notes below.
                            </p>

                            <div className='mt-6'>
                                <label className='block font-bold'>
                                    Notes
                                    <span className='ml-1 text-sm font-normal text-gray-500'>
                                        (optional)
                                    </span>
                                </label>
                                <textarea
                                    className='mt-2 w-full resize-none rounded border p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                                    placeholder='Add any notes about this status change...'
                                    rows={4}
                                    {...register('note')}
                                />
                            </div>
                            
                            <div className='flex gap-[16px]'>
                                <button
                                    onClick={() => setModalOpen(false)}
                                    type='button'
                                    className='mt-[30px] rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    className={`mt-[30px] rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 ${
                                        isSubmitting
                                            ? 'cursor-not-allowed opacity-50'
                                            : 'hover:border-primary-dark hover:bg-primary-dark'
                                    }`}
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting
                                        ? 'Updating...'
                                        : 'Update Status'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GenericStatusUpdateModal;

